import React, { useEffect, useState, useContext, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CartContext } from "../components/CartContext";
import "../styles/OrderConfirmation.css"; // Reuse the same styles
/* global fbq */ // <-- ADD THIS LINE

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderNumber = searchParams.get("orderNumber");
  const { clearCart } = useContext(CartContext);
  const [orderData, setOrderData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Use refs to prevent multiple fetches and cart clearings
  const hasCleared = useRef(false);
  const hasFetched = useRef(false);
  const hasPixelFired = useRef(false);

  // Clear cart only once
  useEffect(() => {
    if (!hasCleared.current) {
      hasCleared.current = true;
      clearCart();
    }
  }, [clearCart]);

  // Fetch order details if available - only once
  useEffect(() => {
    if (orderNumber && !hasFetched.current) {
      hasFetched.current = true;

      const fetchOrderDetails = async () => {
        try {
          const apiUrl =
            process.env.NODE_ENV === "production"
              ? "https://api.gardeniaflowers.md"
              : "http://localhost:5012";

          const response = await fetch(`${apiUrl}/api/orders/${orderNumber}`);

          if (response.ok) {
            const data = await response.json();
            setOrderData(data.order);

            // --- START PURCHASE EVENTS (META + GOOGLE) ---
            if (data.order && !hasPixelFired.current) {
              const purchasedItems = data.order.orderDetails.items;
              const contentIds = purchasedItems.map((item) => String(item.id));
              const totalValue = parseFloat(data.order.orderDetails.total);

              // =========================
              // META PIXEL PURCHASE
              // =========================
              if (window.fbq) {
                fbq("track", "Purchase", {
                  value: totalValue,
                  currency: "MDL",
                  num_items: purchasedItems.length,
                  content_ids: contentIds,
                  content_type: "product",
                  order_id: String(data.order.orderNumber),
                });

                console.log("Meta Purchase fired:", data.order.orderNumber);
              }

              // =========================
              // GOOGLE ANALYTICS PURCHASE
              // =========================
              if (window.gtag) {
                const googleItems = purchasedItems.map((item) => ({
                  item_id: String(item.id),
                  item_name: item.name,
                  price: parseFloat(item.price),
                  quantity: item.quantity,
                }));

                window.gtag("event", "purchase", {
                  transaction_id: String(data.order.orderNumber),
                  value: totalValue,
                  currency: "MDL",
                  items: googleItems,
                });

                console.log("Google Purchase fired:", data.order.orderNumber);
              }

              hasPixelFired.current = true;
            }
            // --- END PURCHASE EVENTS ---
          } else {
            console.error("Failed to fetch order details");
          }
        } catch (error) {
          console.error("Error fetching order details:", error);
        } finally {
          setIsLoading(false);
        }
      };

      fetchOrderDetails();
    } else {
      setIsLoading(false);
    }
  }, [orderNumber]);

  // Helper function to format currency
  const formatCurrency = (amount) => `${amount} lei`;

  // If loading, show a loading spinner
  if (isLoading) {
    return (
      <div className="confirmation-container flex justify-center items-center min-h-[50vh]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Se încarcă detaliile comenzii...</p>
        </div>
      </div>
    );
  }

  // Success page with order details if available
  return (
    <div className="confirmation-container">
      <div className="confirmation-header">
        <div
          className="success-icon text-5xl text-green-600 mb-4 "
          style={{ fontSize: "40px", color: "green" }}
        >
          ✓
        </div>
        <h1 className="confirmation-title">
          Plata a fost efectuată cu succes!
        </h1>
        <div className="order-number-display">
          <strong>Comanda nr. {orderNumber || "N/A"}</strong>
        </div>

        <p className="confirmation-subtitle" style={{ fontSize: "14px" }}>
          Un email de confirmare a fost trimis la adresa dvs. de email.
        </p>
      </div>

      {orderData ? (
        <div className="confirmation-content">
          {/* Order Details Section */}
          <div className="confirmation-section">
            <h2 className="section-title">Detalii Comandă</h2>

            {/* Delivery Method */}
            <div className="delivery-method">
              <h3 className="subsection-title">Metoda de livrare</h3>
              <p className="delivery-type">
                {orderData.isPickup
                  ? "Ridicare de la sediu: Chișinău, Petru Rareș 37"
                  : "Livrare la adresă"}
              </p>
            </div>

            {/* Recipient Information */}
            {!orderData.isPickup && (
              <div className="recipient-info">
                <h3 className="subsection-title">Informații Destinatar</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="info-label">Nume:</span>
                    <span className="info-value">
                      {orderData.recipient.name}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Telefon:</span>
                    <span className="info-value">
                      {orderData.recipient.phone}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Oraș:</span>
                    <span className="info-value">
                      {orderData.recipient.city}
                    </span>
                  </div>
                  {orderData.recipient.address && (
                    <div className="info-item">
                      <span className="info-label">Adresă:</span>
                      <span className="info-value">
                        {orderData.recipient.address}
                      </span>
                    </div>
                  )}
                  {orderData.recipient.message && (
                    <div className="info-item message-item">
                      <span className="info-label">Observații comandă:</span>
                      <span className="info-value">
                        {orderData.recipient.message}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Sender Information */}
            <div className="sender-info">
              <h3 className="subsection-title">Informații Expeditor</h3>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Nume:</span>
                  <span className="info-value">{orderData.sender.name}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Telefon:</span>
                  <span className="info-value">{orderData.sender.phone}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Email:</span>
                  <span className="info-value">{orderData.sender.email}</span>
                </div>
                {orderData.sender.address && (
                  <div className="info-item">
                    <span className="info-label">Adresă:</span>
                    <span className="info-value">
                      {orderData.sender.address}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Order Items */}
            <div className="order-items">
              <h3 className="subsection-title">Produse Comandate</h3>
              {orderData.orderDetails.items.map((item, index) => (
                <div key={index} className="order-item">
                  <div className="item-main">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="item-image"
                    />
                    <div className="item-details">
                      <h4 className="item-name">{item.name}</h4>
                      <p className="item-quantity">
                        Cantitate: {item.quantity}
                      </p>
                      <p className="item-price">{formatCurrency(item.price)}</p>
                    </div>
                  </div>

                  {/* Extras */}
                  {item.extras && item.extras.length > 0 && (
                    <div className="item-extras">
                      <h5 className="extras-title">Extra</h5>
                      {item.extras.map((extra, idx) => (
                        <div key={idx} className="extra-item-conf">
                          <img
                            src={extra.image}
                            alt={extra.name}
                            className="extra-image"
                          />
                          <span className="extra-name">{extra.name}</span>
                          <span className="extra-price">
                            {formatCurrency(extra.price)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Greeting Text */}
                  {item.greetingText && (
                    <div className="greeting-text">
                      <h5 className="greeting-title">Text Felicitare</h5>
                      <p className="greeting-content">{item.greetingText}</p>
                    </div>
                  )}

                  {/* Delivery Details */}
                  {(item.deliveryDate || item.deliveryTime) && (
                    <div className="delivery-details">
                      <h5 className="delivery-title">Detalii Livrare</h5>
                      {item.deliveryDate && (
                        <p className="delivery-date">
                          Data: {item.deliveryDate}
                        </p>
                      )}
                      {item.deliveryTime && (
                        <p className="delivery-time">
                          Ora: {item.deliveryTime}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Order Summary */}
            <div className="order-summary">
              <h3 className="subsection-title">Sumar Comandă</h3>
              <div className="summary-details">
                <div className="summary-row-confirmation">
                  <span className="summary-label">Subtotal:</span>
                  <span className="summary-value">
                    {formatCurrency(orderData.orderDetails.subtotal)}
                  </span>
                </div>
                <div className="summary-row-confirmation">
                  <span className="summary-label">Cost livrare:</span>
                  <span className="summary-value">
                    {formatCurrency(orderData.orderDetails.deliveryFee)}
                  </span>
                </div>
                <div className="summary-row-confirmation total">
                  <span className="summary-label">Total:</span>
                  <span className="summary-value">
                    {formatCurrency(orderData.orderDetails.total)}
                  </span>
                </div>
              </div>
            </div>

            {/* Payment Method */}
            <div className="payment-method">
              <h3 className="subsection-title">Metodă de Plată</h3>
              <p className="payment-type">Plată cu cardul (Paynet)</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="confirmation-content">
          <div className="confirmation-section">
            <div className="mt-8 mb-8 text-center">
              <p className="text-gray-600 mb-4">
                Comanda dumneavoastră a fost procesată cu succes.
              </p>
              <div className="flex justify-center gap-4 mt-6">
                <button
                  onClick={() => navigate("/")}
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                >
                  Înapoi la pagina principală
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentSuccess;
